/**
 * Tier Store
 *
 * Manages user tier features and access control state.
 * Fetches feature configuration from /api/user/features endpoint.
 *
 * Usage:
 * ```typescript
 * import { useTierStore } from './stores/tierStore'
 *
 * const { features, isLoading, hasAccess } = useTierStore()
 *
 * // Check if user has access to a feature
 * const canUsePromptTesting = hasAccess('prompt-testing')
 * ```
 */

import { create } from 'zustand'
import { simpleTrialApi, userApi, tokenManager, extractApiError } from '../lib/api'
import type {
  SimpleTrialStatus,
  FeatureInfo,
  UserFeaturesResponse,
  RateLimitConfig,
} from '../lib/api'
import { logger } from '../lib/logger'

// ============================================================================
// Types
// ============================================================================

// FeatureInfo / UserFeaturesResponse re-exported from lib/api so consumers
// continue to import them from this store; trims duplicate declarations.


interface TierState {
  // State
  features: FeatureInfo[]
  currentTier: string
  enforcementEnabled: boolean
  rateLimits: { chat: RateLimitConfig; auth: RateLimitConfig } | null
  simpleTrial: SimpleTrialStatus | null
  /**
   * §4 free-user cap: this account signed up while the free tier was full and
   * has not been admitted yet. Read from `/api/user/features`, which is the
   * only client-side source for it.
   *
   * Defaults to FALSE and stays false whenever the field is absent — an older
   * control-plane that does not send it must not lock every user out of the
   * composer. The server enforces the block regardless (403 on all three spend
   * surfaces), so failing open here costs a confusing error instead of a
   * spending hole.
   */
  waitlisted: boolean
  isLoading: boolean
  error: string | null
  lastFetched: number | null

  // Actions
  loadFeatures: (options?: { force?: boolean }) => Promise<void>
  loadSimpleTrialInfo: () => Promise<void>
  hasAccess: (featureKey: string, value?: number) => boolean
  getFeatureValue: (featureKey: string) => boolean | number | undefined
  getMinimumTier: (featureKey: string) => string | null
  reset: () => void
}

// ============================================================================
// API
// ============================================================================

async function fetchUserFeatures(): Promise<UserFeaturesResponse> {
  if (!tokenManager.get()) {
    throw new Error('Not authenticated')
  }

  const response = await userApi.getFeatures()

  if (!response.success) {
    throw new Error(extractApiError(response, 'Failed to fetch features'))
  }

  return response.data
}

// ============================================================================
// Store
// ============================================================================

/**
 * Zustand Tier Store
 * Manages user tier features and access control
 */
export const useTierStore = create<TierState>((set, get) => ({
  // Initial state
  features: [],
  currentTier: 'free',
  enforcementEnabled: true,
  rateLimits: null,
  simpleTrial: null,
  waitlisted: false,
  isLoading: false,
  error: null,
  lastFetched: null,

  /**
   * Load user features from API
   * Caches for 5 minutes to avoid excessive API calls
   */
  loadFeatures: async (options?: { force?: boolean }) => {
    const state = get()

    // Skip if recently fetched (within 5 minutes).
    //
    // `force` exists for one caller: the waitlist banner's "Check again"
    // button. Admission happens server-side with no push to the client, so a
    // user admitted 30 seconds ago would sit behind this cache looking at a
    // dead composer for the rest of the window with no way to ask. A button
    // that silently did nothing for five minutes would be worse than no button.
    if (
      !options?.force &&
      state.lastFetched &&
      Date.now() - state.lastFetched < 5 * 60 * 1000 &&
      !state.error
    ) {
      return
    }

    set({ isLoading: true, error: null })

    try {
      const data = await fetchUserFeatures()

      set({
        features: data.features,
        currentTier: data.currentTier,
        enforcementEnabled: data.enforcementEnabled,
        rateLimits: data.rateLimits,
        waitlisted: data.waitlisted ?? false,
        isLoading: false,
        lastFetched: Date.now(),
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load features',
        isLoading: false,
      })
    }
  },

  /**
   * Load FREE-tier evaluation trial info from `GET /api/user/simple-trial`.
   *
   * The surviving trial loader. Its docblock was lost when the paid-trial
   * `loadTrialInfo` above it was deleted — worth restoring precisely because
   * keeping the two trials distinguishable is what the rest of that change was
   * about.
   */
  loadSimpleTrialInfo: async () => {
    try {
      const response = await simpleTrialApi.getStatus()
      if (response.success && response.data) {
        set({ simpleTrial: response.data })
      }
    } catch (error) {
      logger.warn('Failed to load simple trial info', { error: error instanceof Error ? error.message : String(error) })
    }
  },

  /**
   * Check if user has access to a feature
   * Returns true if:
   * - Enforcement is disabled (everyone has access)
   * - Feature is enabled for user's tier
   * - For limits: value is within the limit
   */
  hasAccess: (featureKey: string, value?: number) => {
    const state = get()

    // If enforcement is disabled, everyone has access
    if (!state.enforcementEnabled) {
      return true
    }

    const feature = state.features.find(f => f.key === featureKey)
    if (!feature) {
      return false
    }

    // Boolean feature
    if (feature.type === 'boolean') {
      return feature.enabled
    }

    // Limit feature
    if (feature.type === 'limit') {
      if (value === undefined) {
        // Just checking if feature is available
        return feature.enabled
      }
      // Check if value is within limit
      const limit = feature.limitValue ?? 0
      return value <= limit || limit === -1 // -1 represents Infinity
    }

    return false
  },

  /**
   * Get the raw value of a feature
   */
  getFeatureValue: (featureKey: string) => {
    const state = get()
    const feature = state.features.find(f => f.key === featureKey)

    if (!feature) {
      return undefined
    }

    if (feature.type === 'limit') {
      return feature.limitValue
    }

    return feature.enabled
  },

  /**
   * Get the minimum tier required for a feature
   */
  getMinimumTier: (featureKey: string) => {
    const state = get()
    const feature = state.features.find(f => f.key === featureKey)
    return feature?.minimumTier || null
  },

  /**
   * Reset store state (on logout)
   */
  reset: () => {
    set({
      features: [],
      currentTier: 'free',
      enforcementEnabled: true,
      rateLimits: null,
      simpleTrial: null,
      waitlisted: false,
      isLoading: false,
      error: null,
      lastFetched: null,
    })
  },
}))

export {type FeatureInfo, type UserFeaturesResponse} from '../lib/api'